import { Button } from "@cleanbudget/ui/components/button";
import { IconTrash } from "@tabler/icons-react";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BackLink } from "@/components/back-link";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { PageHeader } from "@/components/page-header";
import { orpc } from "@/lib/orpc";
import { FaqItemForm, type FaqItemValues } from "./-components/faq-item-form";

const faqItemQuery = (id: string) => orpc.faqItem.get.queryOptions({ input: { id } });

export const Route = createFileRoute("/_admin/content/faq/$id")({
	loader: ({ context, params }) =>
		Promise.all([
			context.queryClient.ensureQueryData(faqItemQuery(params.id)),
			context.queryClient.ensureQueryData(orpc.category.list.queryOptions()),
		]),
	component: EditFaqItemPage,
});

/** Edit a question, or delete it. */
function EditFaqItemPage() {
	const { t } = useTranslation("faqItem");
	const { id } = Route.useParams();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { data: item, refetch } = useSuspenseQuery(faqItemQuery(id));
	const [loads, setLoads] = useState(0);
	const update = useMutation({
		...orpc.faqItem.update.mutationOptions(),
		onSuccess: (saved) => {
			queryClient.setQueryData(faqItemQuery(id).queryKey, saved);
			return queryClient.invalidateQueries({ queryKey: orpc.faqItem.list.key() });
		},
	});
	const remove = useMutation({
		...orpc.faqItem.delete.mutationOptions(),
		// The list is where this goes next: have it fresh, without the deleted question.
		onSuccess: () => queryClient.invalidateQueries({ queryKey: orpc.faqItem.list.key(), refetchType: "all" }),
	});

	const onSubmit = async (values: FaqItemValues) => {
		await update.mutateAsync({ id, version: item.version, ...values });
	};

	return (
		<div className="flex max-w-5xl flex-col gap-6">
			<PageHeader back={<BackLink to="/content/faq">{t("title")}</BackLink>} title={t("edit.title")} />
			{/* A new key after a reload starts the form again from what is stored now. */}
			<FaqItemForm
				key={loads}
				item={item}
				submitLabel={t("edit.submit")}
				onSubmit={onSubmit}
				onReload={async () => {
					await refetch();
					setLoads((count) => count + 1);
				}}
				actions={
					<ConfirmDialog
						trigger={
							<Button type="button" variant="destructive">
								<IconTrash aria-hidden />
								{t("delete.button")}
							</Button>
						}
						title={t("delete.title")}
						description={t("delete.description", { question: item.question })}
						confirmLabel={t("delete.confirm")}
						onConfirm={async () => {
							await remove.mutateAsync({ id });
							await navigate({ to: "/content/faq" });
							queryClient.removeQueries({ queryKey: faqItemQuery(id).queryKey });
						}}
					/>
				}
			/>
		</div>
	);
}
